import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { MessageSquare, Send, Bot, User, Sparkles, Globe, Loader2, Mic, MicOff, AlertCircle, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LanguageSelector from '../components/LanguageSelector';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// ============================================================================
// LANGUAGE LOCALE MAP - For Speech Recognition
// ============================================================================
const LOCALE_MAP = {
  'en-IN': { code: 'en-IN', name: 'English (India)', nativeName: 'English' },
  'hi-IN': { code: 'hi-IN', name: 'Hindi', nativeName: 'हिन्दी' },
  'bn-IN': { code: 'bn-IN', name: 'Bengali', nativeName: 'বাংলা' },
  'ta-IN': { code: 'ta-IN', name: 'Tamil', nativeName: 'தமிழ்' },
  'te-IN': { code: 'te-IN', name: 'Telugu', nativeName: 'తెలుగు' },
  'mr-IN': { code: 'mr-IN', name: 'Marathi', nativeName: 'मराठी' },
  'gu-IN': { code: 'gu-IN', name: 'Gujarati', nativeName: 'ગુજરાતી' },
  'kn-IN': { code: 'kn-IN', name: 'Kannada', nativeName: 'ಕನ್ನಡ' },
  'ml-IN': { code: 'ml-IN', name: 'Malayalam', nativeName: 'മലയാളം' },
  'or-IN': { code: 'or-IN', name: 'Odia', nativeName: 'ଓଡ଼ିଆ' },
  'pa-IN': { code: 'pa-IN', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ' },
  'ur-IN': { code: 'ur-IN', name: 'Urdu', nativeName: 'اردو' }
};

// Listening hint text in different languages
const LISTENING_HINTS = {
  'en-IN': 'Listening… tap mic to stop',
  'hi-IN': 'सुन रहा हूँ… रोकने के लिए माइक टैप करें',
  'bn-IN': 'শুনছি… থামাতে মাইক ট্যাপ করুন',
  'ta-IN': 'கேட்கிறேன்… நிறுத்த மைக் தட்டவும்',
  'te-IN': 'వింటున్నాను… ఆపడానికి మైక్ నొక్కండి',
  'mr-IN': 'ऐकत आहे… थांबवण्यासाठी माइक टॅप करा',
  'gu-IN': 'સાંભળી રહ્યો છું… રોકવા માટે માઇક ટેપ કરો',
  'kn-IN': 'ಕೇಳುತ್ತಿದ್ದೇನೆ… ನಿಲ್ಲಿಸಲು ಮೈಕ್ ಟ್ಯಾಪ್ ಮಾಡಿ',
  'ml-IN': 'കേൾക്കുന്നു… നിർത്താൻ മൈക്ക് ടാപ്പ് ചെയ്യുക',
  'or-IN': 'ଶୁଣୁଛି… ବନ୍ଦ କରିବାକୁ ମାଇକ୍ ଟ୍ୟାପ୍ କରନ୍ତୁ'
};

// Error messages
const ERROR_MESSAGES = {
  'not-supported': 'Voice dictation is not supported in this browser. Please use Chrome or Edge.',
  'not-allowed': 'Microphone access denied. Enable it in browser settings.',
  'no-speech': 'No speech detected. Please try again.',
  'audio-capture': 'Microphone not available. Please check your device.',
  'network': 'Network issue. Please check your connection.',
  'aborted': 'Voice input was cancelled.',
  'default': 'Voice input error. Please try again.'
};

// ============================================================================
// SPEECH DICTATION HOOK - ChatGPT-like Web Speech API
// ============================================================================
const useSpeechDictation = (locale = 'en-IN') => {
  // Check browser support synchronously during initialization
  const SpeechRecognitionAPI = typeof window !== 'undefined' 
    ? (window.SpeechRecognition || window.webkitSpeechRecognition) 
    : null;
  
  const [isListening, setIsListening] = useState(false);
  const [isSupported] = useState(!!SpeechRecognitionAPI);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const currentInterimRef = useRef('');

  // Log support status on mount
  useEffect(() => {
    if (!SpeechRecognitionAPI) {
      console.warn('❌ SpeechRecognition not supported in this browser');
    } else {
      console.log('  SpeechRecognition supported');
    }
  }, [SpeechRecognitionAPI]);

  // Initialize recognition when locale changes
  useEffect(() => {
    if (!isSupported || !SpeechRecognitionAPI) return;

    const recognition = new SpeechRecognitionAPI();
    
    // Configuration for ChatGPT-like behavior
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.lang = locale;

    recognition.onstart = () => {
      console.log('🎤 Speech recognition started');
      setIsListening(true);
      setError(null);
      finalTranscriptRef.current = '';
      setTranscript('');
      setInterimTranscript('');
    };

    recognition.onresult = (event) => {
      let interim = '';
      let final = finalTranscriptRef.current;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          final += result[0].transcript + ' ';
          finalTranscriptRef.current = final;
        } else {
          interim += result[0].transcript;
        }
      }

      setTranscript(final.trim());
      setInterimTranscript(interim);
      currentInterimRef.current = interim;
    };

    recognition.onerror = (event) => {
      console.error('❌ Speech recognition error:', event.error);
      
      if (event.error === 'not-allowed') {
        setPermissionDenied(true);
      }
      
      setError(ERROR_MESSAGES[event.error] || ERROR_MESSAGES['default']);
      setIsListening(false);
    };

    recognition.onend = () => {
      console.log('🎤 Speech recognition ended');
      setIsListening(false);
      // Commit final transcript
      const finalText = (finalTranscriptRef.current + ' ' + currentInterimRef.current).trim();
      if (finalText) {
        setTranscript(finalText);
      }
      setInterimTranscript('');
      currentInterimRef.current = '';
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors on cleanup
        }
      }
    };
  }, [locale, isSupported, SpeechRecognitionAPI]);

  const startListening = useCallback(() => {
    if (!isSupported) {
      setError(ERROR_MESSAGES['not-supported']);
      return;
    }

    if (permissionDenied) {
      setError(ERROR_MESSAGES['not-allowed']);
      return;
    }

    if (recognitionRef.current && !isListening) {
      try {
        setError(null);
        finalTranscriptRef.current = '';
        setTranscript('');
        setInterimTranscript('');
        recognitionRef.current.start();
      } catch (e) {
        console.error('Failed to start recognition:', e);
        setError('Failed to start voice input. Please try again.');
      }
    }
  }, [isSupported, isListening, permissionDenied]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current && isListening) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.error('Failed to stop recognition:', e);
      }
    }
  }, [isListening]);

  const cancelListening = useCallback(() => {
    if (recognitionRef.current && isListening) {
      try {
        recognitionRef.current.abort();
        finalTranscriptRef.current = '';
        setTranscript('');
        setInterimTranscript('');
      } catch (e) {
        console.error('Failed to cancel recognition:', e);
      }
    }
  }, [isListening]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearTranscript = useCallback(() => {
    finalTranscriptRef.current = '';
    setTranscript('');
    setInterimTranscript('');
  }, []);

  // Get combined transcript (final + interim)
  const fullTranscript = transcript + (interimTranscript ? ' ' + interimTranscript : '');

  return {
    isListening,
    isSupported,
    transcript: fullTranscript.trim(),
    finalTranscript: transcript,
    interimTranscript,
    error,
    permissionDenied,
    startListening,
    stopListening,
    cancelListening,
    clearError,
    clearTranscript
  };
};

// ============================================================================
// VOICE WAVE ANIMATION COMPONENT
// ============================================================================
const VoiceWaveAnimation = ({ isActive }) => {
  if (!isActive) return null;
  
  // Pre-defined heights for wave bars (instead of random)
  const barHeights = [8, 14, 10, 16];
  
  return (
    <div className="flex items-center justify-center space-x-1 h-4">
      {barHeights.map((height, i) => (
        <div
          key={i}
          className="w-1 bg-red-500 rounded-full"
          style={{
            animation: 'pulse 0.5s ease-in-out infinite alternate',
            animationDelay: `${i * 0.1}s`,
            height: `${height}px`
          }}
        />
      ))}
    </div>
  );
};

// ============================================================================
// DICTATE MIC BUTTON COMPONENT - ChatGPT-like
// ============================================================================
const DictateMicButton = ({ 
  isListening, 
  isSupported, 
  onClick, 
  disabled,
  error,
  className = ''
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleClick = () => {
    if (!isSupported) {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 3000);
      return;
    }
    onClick?.();
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        aria-label={isListening ? "Stop dictation" : "Start voice dictation"}
        className={`
          relative p-2 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
          ${isListening 
            ? 'bg-red-500 hover:bg-red-600 text-white focus:ring-red-500' 
            : isSupported
              ? 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 focus:ring-blue-500'
              : 'bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${className}
        `}
      >
        {/* Pulsing ring when listening */}
        {isListening && (
          <span className="absolute inset-0 rounded-full animate-ping bg-red-400 opacity-75" />
        )}
        
        {/* Icon */}
        <span className="relative">
          {isListening ? (
            <div className="flex items-center justify-center w-5 h-5">
              <VoiceWaveAnimation isActive={true} />
            </div>
          ) : (
            <Mic className={`w-5 h-5 ${!isSupported ? 'opacity-50' : ''}`} />
          )}
        </span>
      </button>

      {/* Tooltip for unsupported browsers */}
      {showTooltip && !isSupported && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg shadow-lg whitespace-nowrap z-50">
          <div className="flex items-center space-x-1">
            <AlertCircle className="w-3 h-3" />
            <span>Dictation not supported in this browser</span>
          </div>
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800" />
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN CHATBOT COMPONENT
// ============================================================================
const ChatbotMultilingual = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I\'m ReasonBot, your AI assistant for Reason RetailIQ. I can communicate with you in 10+ Indian languages. You can type your question or use the 🎤 mic button to dictate. How can I assist you today?',
      timestamp: new Date().toISOString()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState('en-IN');
  const [autoSendOnDictation, setAutoSendOnDictation] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Speech Dictation Hook
  const {
    isListening,
    isSupported,
    transcript,
    interimTranscript,
    error: dictationError,
    permissionDenied,
    startListening,
    stopListening,
    cancelListening,
    clearError,
    clearTranscript
  } = useSpeechDictation(selectedLocale);

  // Update input when transcript changes
  useEffect(() => {
    if (transcript && isListening) {
      setInput(transcript);
    }
  }, [transcript, isListening]);

  // Handle dictation completion
  useEffect(() => {
    if (!isListening && transcript) {
      setInput(transcript);
      clearTranscript();
      
      // Auto-send if enabled
      if (autoSendOnDictation && transcript.trim()) {
        // Small delay to allow user to see the transcript
        // Note: handleSend is stable after initial render, safe to call via ref pattern
        setTimeout(() => {
          // Direct call instead of dependency - avoids stale closure
          document.querySelector('[data-send-button]')?.click();
        }, 500);
      } else {
        // Focus input for editing
        inputRef.current?.focus();
      }
    }
     
  }, [isListening, transcript, autoSendOnDictation, clearTranscript]);

  // Handle ESC key to stop dictation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isListening) {
        cancelListening();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isListening, cancelListening]);

  // Handle page visibility change - stop recognition when tab is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isListening) {
        stopListening();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isListening, stopListening]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Scroll when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load saved locale ONCE on mount
  useEffect(() => {
    const savedLocale = localStorage.getItem('preferredLocale');
    if (savedLocale) {
      setSelectedLocale(savedLocale);
    } else {
      // Try browser locale
      const browserLocale = navigator.language;
      if (LOCALE_MAP[browserLocale]) {
        setSelectedLocale(browserLocale);
      } else if (browserLocale.startsWith('hi')) {
        setSelectedLocale('hi-IN');
      } else if (browserLocale.startsWith('bn')) {
        setSelectedLocale('bn-IN');
      } else if (browserLocale.startsWith('ta')) {
        setSelectedLocale('ta-IN');
      }
    }
  }, []);

  // Handle language change
  const handleLanguageChange = useCallback((locale) => {
    setSelectedLocale(locale);
    localStorage.setItem('preferredLocale', locale);
    
    const languageNames = {
      'en-IN': 'English',
      'hi-IN': 'Hindi (हिन्दी)',
      'bn-IN': 'Bengali (বাংলা)',
      'ta-IN': 'Tamil (தமிழ்)',
      'te-IN': 'Telugu (తెలుగు)',
      'mr-IN': 'Marathi (मराठी)',
      'gu-IN': 'Gujarati (ગુજરાતી)',
      'kn-IN': 'Kannada (ಕನ್ನಡ)',
      'ml-IN': 'Malayalam (മലയാളം)',
      'or-IN': 'Odia (ଓଡ଼ିଆ)'
    };
    
    setMessages(prev => [...prev, {
      role: 'system',
      content: `Language changed to ${languageNames[locale]}. I will now respond in this language.`,
      timestamp: new Date().toISOString()
    }]);
  }, []);

  // Toggle dictation
  const handleDictationToggle = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      // Clear input before starting new dictation
      setInput('');
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  const handleSend = async () => {
    // Stop dictation if active
    if (isListening) {
      stopListening();
      // Wait for final transcript
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (!input.trim()) return;
    if (loading) return;

    const currentInput = input.trim();
    
    const userMessage = { 
      role: 'user', 
      content: currentInput,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    
    setInput('');
    clearTranscript();
    setLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/chatbot/query`,
        {
          query: currentInput,
          language: selectedLocale.split('-')[0]
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const responseData = response.data;
      const assistantContent = responseData.answer 
        || responseData.response 
        || 'I apologize, but I received an empty response. Please try again.';
      
      const assistantMessage = { 
        role: 'assistant', 
        content: assistantContent,
        intent: responseData.intent,
        confidence: responseData.confidence,
        playbook_used: responseData.playbook_used,
        data_points: responseData.data_points || 0,
        tables_used: responseData.tables_used || [],
        follow_up_suggestions: responseData.follow_up_suggestions || [],
        timestamp: responseData.timestamp || new Date().toISOString()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
      console.log('  Chatbot response received:', {
        query: currentInput,
        intent: responseData.intent,
        playbook: responseData.playbook_used,
        data_points: responseData.data_points,
        responseLength: assistantContent.length
      });
      
    } catch (error) {
      console.error('❌ Chatbot error:', {
        error: error.response?.data || error.message,
        status: error.response?.status,
        query: currentInput,
        language: selectedLocale
      });
      
      const errorDetail = error.response?.data?.detail || error.message || 'Unknown error';
      const errorMessage = { 
        role: 'assistant', 
        content: `Sorry, I encountered an error: ${errorDetail}. Please ensure the OpenAI API key is configured and try again.`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Example questions by language
  const exampleQuestions = {
    'en-IN': [
      "What are the latest insights from AI agents?",
      "Show me recent sales trends",
      "What inventory issues need attention?",
      "Summarize recent recommendations"
    ],
    'hi-IN': [
      "AI एजेंटों से नवीनतम जानकारी क्या है?",
      "हालिया बिक्री के रुझान दिखाएं",
      "किस इन्वेंट्री मुद्दों पर ध्यान देने की जरूरत है?",
      "हालिया सिफारिशों का सारांश दें"
    ],
    'bn-IN': [
      "AI এজেন্টদের থেকে সর্বশেষ অন্তর্দৃষ্টি কি?",
      "সাম্প্রতিক বিক্রয় প্রবণতা দেখান",
      "কোন ইনভেন্টরি সমস্যাগুলির দিকে মনোযোগ প্রয়োজন?",
      "সাম্প্রতিক সুপারিশগুলির সারসংক্ষেপ দিন"
    ],
    'ta-IN': [
      "AI முகவர்களிடமிருந்து சமீபத்திய நுண்ணறிவுகள் என்ன?",
      "சமீபத்திய விற்பனை போக்குகளைக் காட்டு",
      "எந்த சரக்கு பிரச்சினைகளுக்கு கவனம் தேவை?",
      "சமீபத்திய பரிந்துரைகளை சுருக்கமாகக் கூறு"
    ]
  };

  const currentExamples = exampleQuestions[selectedLocale] || exampleQuestions['en-IN'];

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
            <MessageSquare className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">ReasonBot</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">AI-Powered Retail Assistant</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Auto-send toggle */}
          <label className="flex items-center space-x-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={autoSendOnDictation}
              onChange={(e) => setAutoSendOnDictation(e.target.checked)}
              className="rounded border-slate-300 text-blue-500 focus:ring-blue-500"
            />
            <span>Auto-send dictation</span>
          </label>
          
          <LanguageSelector
            value={selectedLocale}
            onLanguageChange={handleLanguageChange}
          />
        </div>
      </div>

      {/* Chat Container */}
      <Card className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-slate-900 shadow-xl">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex items-start space-x-3 ${
                message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
              } ${message.role === 'system' ? 'justify-center' : ''}`}
            >
              {/* Avatar */}
              {message.role !== 'system' && (
                <div
                  className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center shadow-md ${
                    message.role === 'user'
                      ? 'bg-gradient-to-br from-blue-500 to-blue-600'
                      : 'bg-gradient-to-br from-purple-500 to-blue-600'
                  }`}
                >
                  {message.role === 'user' ? (
                    <User className="h-5 w-5 text-white" />
                  ) : (
                    <Bot className="h-5 w-5 text-white" />
                  )}
                </div>
              )}

              {/* Message Content */}
              <div
                className={`flex-1 max-w-3xl ${
                  message.role === 'user' ? 'text-right' : ''
                } ${message.role === 'system' ? 'text-center' : ''}`}
                style={{ fontFamily: 'var(--app-font-family, "Noto Sans")' }}
              >
                <div
                  className={`inline-block px-5 py-3 rounded-2xl shadow-sm ${
                    message.role === 'user'
                      ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white'
                      : message.role === 'system'
                      ? 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-sm italic'
                      : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700'
                  }`}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                  
                  {/* Sources indicator */}
                  {message.data_points > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-300 dark:border-slate-600">
                      <p className="text-xs opacity-75 flex items-center">
                        <Sparkles className="h-3 w-3 mr-1" />
                        Retrieved from {message.data_points} agent insights
                      </p>
                    </div>
                  )}
                  
                  {/* Follow-up suggestions */}
                  {message.follow_up_suggestions && message.follow_up_suggestions.length > 0 && (
                    <div className="mt-3 pt-2 border-t border-slate-200 dark:border-slate-600">
                      <p className="text-xs opacity-70 mb-2">You can also ask:</p>
                      <div className="flex flex-wrap gap-1">
                        {message.follow_up_suggestions.slice(0, 3).map((suggestion, i) => (
                          <button
                            key={i}
                            onClick={() => {
                              setInput(suggestion);
                              inputRef.current?.focus();
                            }}
                            className="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded-full hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Timestamp */}
                  {message.timestamp && message.role !== 'system' && (
                    <div className="mt-1 text-xs opacity-50">
                      {new Date(message.timestamp).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {/* Loading indicator */}
          {loading && (
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-purple-500 to-blue-600">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <div className="bg-white dark:bg-slate-800 px-5 py-3 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700">
                <div className="flex space-x-2">
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Example Questions - shown only at start */}
        {messages.length <= 2 && (
          <div className="px-4 pb-2">
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {currentExamples.map((question, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setInput(question);
                    inputRef.current?.focus();
                  }}
                  className="text-xs"
                  style={{ fontFamily: 'var(--app-font-family, "Noto Sans")' }}
                >
                  {question}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Dictation Error Toast */}
        {dictationError && (
          <div className="mx-4 mb-2 flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{dictationError}</span>
            </div>
            <button onClick={clearError} className="text-red-500 hover:text-red-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Listening Indicator */}
        {isListening && (
          <div className="mx-4 mb-2 flex items-center justify-center p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
              <div className="relative">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <div className="absolute inset-0 w-3 h-3 bg-red-500 rounded-full animate-ping" />
              </div>
              <span className="text-sm font-medium">
                {LISTENING_HINTS[selectedLocale] || LISTENING_HINTS['en-IN']}
              </span>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <div className="flex items-center space-x-2 bg-slate-50 dark:bg-slate-700 rounded-xl px-4 py-2 border border-slate-200 dark:border-slate-600 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
            {/* Input Field */}
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                isListening 
                  ? (interimTranscript || 'Speak now...') 
                  : `Ask ReasonBot in ${LOCALE_MAP[selectedLocale]?.nativeName || 'English'}...`
              }
              className={`flex-1 bg-transparent border-none focus:outline-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 ${
                isListening ? 'text-red-600 dark:text-red-400' : ''
              }`}
              disabled={loading}
              style={{ fontFamily: 'var(--app-font-family, "Noto Sans")' }}
            />
            
            {/* Dictate Mic Button - ChatGPT-like */}
            <DictateMicButton
              isListening={isListening}
              isSupported={isSupported}
              onClick={handleDictationToggle}
              disabled={loading}
              error={dictationError}
            />
            
            {/* Send Button */}
            <Button
              data-send-button
              onClick={handleSend}
              disabled={loading || (!input.trim() && !isListening)}
              className="px-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-lg"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
          
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 text-center">
            ReasonBot uses AI agent insights. 
            {isSupported && <span> Press <kbd className="px-1 py-0.5 bg-slate-200 dark:bg-slate-600 rounded text-xs">🎤</kbd> to dictate or <kbd className="px-1 py-0.5 bg-slate-200 dark:bg-slate-600 rounded text-xs">ESC</kbd> to cancel.</span>}
            {!isSupported && <span className="text-amber-500"> (Dictation requires Chrome or Edge)</span>}
          </p>
        </div>
      </Card>
    </div>
  );
};

export default ChatbotMultilingual;
